import Big from "big.js";
import { IWalletDetails } from "@market/interfaces/IWallet";
import {
  isObject,
  countDecimalPlaces,
  numberWithDelimiter,
  renameMainCurrency,
  normalizeBankAccountNumber,
} from "@features/utils/functions";
import {
  IUseWithdrawForm,
  IWithdrawFees,
} from "@funds/components/WithdrawForm/IWithdrawForm";
import { MAIN_CURRENCY, MAIN_CURRENCY_LABEL } from "@bundles/consts";
import IPurchaseExchangeRates from "@appInterfaces/IPurchaseExchangeRates";
import { IUseCryptoWallets } from "@funds/components/CryptoWallets/ICryptoWallets";
import { IBankFormInputs } from "@funds/components/BankForm/IBankForm";
import { ITradingSettings } from "@funds/components/ExchangeForm/IExchangeForm";
import {
  CRYPTO_PLACES,
  decimalPlaces,
} from "@appComponents/CurrencyElement/CurrencyElement";

import * as WAValidator from "wallet-address-validator";

export const restrictedByUserStatementKey = "restricted_by_user_statement";

export const userCanWithdraw = (
  wallet: IWalletDetails,
  noAction = true,
  verificationUrl?: string
): true | string => {
  if (wallet.abilities.can_withdraw === true) {
    return true;
  }
  // Related with TP-1169
  if (wallet.currency === "ldcc") {
    return `Token can be withdrawn through the <a href="/bfwallet">BF Wallet</a>`;
  }

  return sortAbilitiesByPriority(wallet.abilities.can_withdraw)[0];
};

export const userCanDeposit = (
  wallet: IWalletDetails,
  noAction = true,
  verificationUrl?: string
): true | string => {
  if (wallet.abilities.can_deposit === true) {
    return true;
  }

  return sortAbilitiesByPriority(wallet.abilities.can_deposit)[0];
};

export const userCanBuy = (
  wallet: IWalletDetails,
  noAction = true,
  verificationUrl?: string
) => {
  const abilityKeyName = "can_buy";
  const abilitiesList = wallet.abilities[abilityKeyName];

  if (typeof abilitiesList !== "boolean") {
    return sortAbilitiesByPriority(abilitiesList)[0];
  }

  return true;
};

export const userCanSell = (wallet: IWalletDetails) => {
  const abilitiesList = wallet.abilities["can_sell"];

  if (typeof abilitiesList !== "boolean") {
    return abilitiesList[0];
  }

  return true;
};

const sortAbilitiesByPriority = (data: string[]) => {
  const restrictedByCurrency = data.indexOf("restricted_by_currency");

  if (restrictedByCurrency !== -1) {
    return [
      data[restrictedByCurrency],
      ...data.filter((key) => key !== "restricted_by_currency"),
    ];
  }

  return data;
};

const BICRegex = /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/;

export const bankFormValidation = (
  values: IBankFormInputs,
  bicExternalValidationEnabled: boolean = false
) => {
  const errors: Partial<
    IBankFormInputs & { bic_swift_external: string; base: string }
  > = {};
  const requiredMessage = "Required field.";

  const bicValidation = () => {
    if (!values.bic_swift) {
      return (errors.bic_swift = requiredMessage);
    }
    if (!BICRegex.test(values.bic_swift)) {
      return (errors.bic_swift = "The BIC/SWIFT is wrong.");
    }
    if (bicExternalValidationEnabled) {
      return (errors.bic_swift_external = "Validation in progress");
    }

    return undefined;
  };

  const accountNumberValidation = () => {
    if (!normalizeBankAccountNumber(values.account_number || "")) {
      errors.account_number = requiredMessage;
    }
  };

  accountNumberValidation();
  bicValidation();

  return errors;
};

export const walletFormValidation = (
  values: IUseWithdrawForm["values"] | IUseCryptoWallets["values"],
  currency: string
) => {
  const errors: IUseWithdrawForm["errors"] = {};
  const requiredMessage = "Required field.";

  if (!!currency && !!values.address) {
    try {
      if (!WAValidator.validate(values.address, currency?.toUpperCase())) {
        errors.address = "Address is not valid.";
      }
    } catch (error) {
      console.log("Couldn't validate the address");
    }
  } else {
    if (!values.address) {
      errors.address = requiredMessage;
    } else if (values.address.length < 3) {
      errors.address = "The field is too short.";
    }
  }

  // Currency validation
  if (!currency) {
    errors.currency = requiredMessage;
  }

  if (
    currency === "exit" &&
    [
      "tbeadzirzilx3nscjj7kmm3j6wg7ourery45b2scekogksa4ozbd2nkin4",
      "tzerovz4ftebncaxdyjd5laz2zxgmibfgx35zeagp7zzzytrviho7cajty",
    ].includes(values?.address?.toLowerCase().trim() || "")
  ) {
    errors.address = `This address corresponds to the Securitize or tZero deposit address,
        you cannot use it to withdraw your EXIT tokens, please use your own whitelisted address instead.`;
  }

  return errors;
};

export const purchaseAmountValidation = (
  userWallet: IWalletDetails,
  exchangeRates: IPurchaseExchangeRates,
  totalToPay: Big,
  purchaseWallet: IWalletDetails,
  minPurchaseUsdValue: number,
  updatedAmount?: string | number
) => {
  if (updatedAmount === undefined) return;
  if (isNaN(parseInt(updatedAmount as string, 10)) || Big(updatedAmount).eq(0))
    return `Required field.`;

  if (totalToPay.gt(userWallet.balance || 0)) {
    return `Your available balance is not sufficient for this purchase,
    <a href="deposit?currency=${purchaseWallet.currency}">click here to make a deposit.</a>`;
  }

  if (Big(updatedAmount).gt(purchaseWallet.available_purchase_cap!)) {
    return `Unfortunately, you've exceeded the purchase cap.`;
  }

  if (Big(updatedAmount).div(exchangeRates.bft_usd).lt(minPurchaseUsdValue)) {
    const minimumPurchase = Big(minPurchaseUsdValue)
      .times(exchangeRates.bft_usd)
      .round(0, 3);
    return `The minimum purchase amount is ${minimumPurchase.toString()} BFT.`;
  }

  return null;
};

export const withdrawAmountValidation = (
  values: IUseWithdrawForm["values"],
  userWallet: IWalletDetails,
  fees: IWithdrawFees[] | undefined,
  exchangeRate?: number
) => {
  if (!values.amount || Big(Number(values.amount)).lte(0)) {
    return "Required field.";
  }

  if (Big(Number(values.amount)).gt(userWallet.balance)) {
    return "Your available balance is not sufficient for this withdrawal.";
  }

  const isMainCurrency = userWallet.currency === MAIN_CURRENCY;
  const currency = MAIN_CURRENCY_LABEL.toUpperCase();
  const selectedWalletCurrency =
    isMainCurrency && values.selectedWalletType === "normal"
      ? userWallet.currency
      : values.selectedWalletCurrency || userWallet.currency;

  const selectedWalletFeeObject = getWalletFeeObject(
    fees,
    selectedWalletCurrency
  );
  const selectedWalletFee = getWalletFee(
    selectedWalletFeeObject,
    values.amount
  );
  const walletCurrency = renameMainCurrency(userWallet.currency).toUpperCase();
  const defaultMinimumAmount =
    fees?.find((fee) => fee.currency === "default")?.minimum_amount || 0;
  const defaultMinimumAmountSetting = exchangeRate
    ? Big(defaultMinimumAmount).div(exchangeRate)
    : 0;

  const minimumAmountSetting =
    selectedWalletFeeObject && selectedWalletFeeObject.currency !== "default"
      ? selectedWalletFeeObject.minimum_amount
      : defaultMinimumAmountSetting;

  const decimalPlacesLimit = isMainCurrency
    ? decimalPlaces[MAIN_CURRENCY]
    : CRYPTO_PLACES;
  const isInvalidDecimalPlaces = validateDecimalPlaces(
    countDecimalPlaces(values.amount),
    decimalPlacesLimit
  );

  if (isInvalidDecimalPlaces) return isInvalidDecimalPlaces;

  const minimum = Big(minimumAmountSetting).round(decimalPlacesLimit);
  const isInvalidMinimumWithdraw = validateMinimumWithdraw(
    values.amount,
    minimum,
    walletCurrency
  );

  if (isInvalidMinimumWithdraw) return isInvalidMinimumWithdraw;

  if (isMainCurrency) {
    if (
      values.selectedWalletType === "normal" &&
      Big(Number(values.amount)).lte(Math.ceil(selectedWalletFee))
    ) {
      return `Withdraw amount must be greater than fee: ${selectedWalletFee} ${currency}`;
    }
  }

  if (Big(values.amount).lt(selectedWalletFee)) {
    return `Withdraw amount must be greater than fee: ${selectedWalletFee} ${currency}`;
  }

  return null;
};

export const exchangeAmountValidation = (
  amount: string | number | null,
  outcomeCurrencyWallet: IWalletDetails | undefined,
  settings: ITradingSettings | undefined | null
) => {
  if (!outcomeCurrencyWallet) {
    return "Please set the set the currency from the select options";
  }

  const amountNumber = Number(amount);
  const isMainCurrency = outcomeCurrencyWallet.currency === MAIN_CURRENCY;

  if (!amount || Big(amountNumber).lte(0)) {
    return "Required field.";
  }

  if (Big(amountNumber).gt(outcomeCurrencyWallet.balance)) {
    return `Your available balance is not sufficient for this trade,
    <a href="deposit?currency=${outcomeCurrencyWallet.currency}">click here to make a deposit.</a>`;
  }

  if (settings) {
    if (
      Big(settings.minimum_amount_per_trade).gt(
        settings.maximum_amount_per_trade
      )
    ) {
      return `The exchange of these currencies is temporarily unavailable.\
      Please try again later. If the problem persists,\
      <a href="/support" title="Support link"> please contact our support.</a>`;
    }

    if (Big(amountNumber).lt(settings.minimum_amount_per_trade)) {
      return `The minimum possible amount is ${numberWithDelimiter(
        settings.minimum_amount_per_trade
      )}`;
    }

    if (Big(amountNumber).gt(settings.maximum_amount_per_trade)) {
      return `The maximum possible amount is ${numberWithDelimiter(
        settings.maximum_amount_per_trade
      )}`;
    }
  }

  const decimalPlaces = countDecimalPlaces(amountNumber);
  const decimalPlacesLimit = isMainCurrency ? 2 : 8;
  const isInvalidDecimalPlaces = validateDecimalPlaces(
    decimalPlaces,
    decimalPlacesLimit
  );

  if (isInvalidDecimalPlaces) return isInvalidDecimalPlaces;
  return "";
};

export const getWalletFeeObject = (
  fees: IWithdrawFees[] | undefined,
  currency?: string
) => {
  if (fees && currency) {
    const selectedCurrency = currency === "bfusd" ? "usdt" : currency;
    const fee = fees.find((el) => el.currency === selectedCurrency);

    if (fee) {
      return fee;
    }

    return fees.find((el) => el.currency === "default");
  }

  return undefined;
};

export const getWalletFee = (
  feeObject: IWithdrawFees | undefined,
  value: number
) => {
  if (!feeObject) {
    return 0;
  }

  const percentFee = Big(value).times(feeObject.fees.percent_fee).div(100);

  // Get the bigger one
  if (feeObject.fees.calculation_type === "max") {
    if (Big(percentFee).gt(feeObject.fees.flat_fee)) {
      return Number(percentFee);
    }

    return feeObject.fees.flat_fee;
  }

  // Get sum of both
  return Number(Big(percentFee).add(feeObject.fees.flat_fee));
};

export const validateMinimumWithdraw = (
  amount: number,
  minimum: Big,
  currency: string
) =>
  Big(Number(amount)).lt(minimum)
    ? `Minimum Withdrawal Amount: ${minimum} ${currency?.toLocaleUpperCase()}`
    : null;

export const validateDecimalPlaces = (
  decimalPlaces: number,
  decimalLimit: number
) => (decimalPlaces > decimalLimit ? "Too many decimal places." : null);

export const addressRegex = (
  fees: IWithdrawFees[] | undefined,
  currency: string | undefined
) => {
  const regexForCurrency = ["bft", "bfusd"].includes(currency || "")
    ? "eth"
    : currency;

  return fees?.find((fee) => fee.currency === regexForCurrency)?.address_regex;
};
